<template>
    <div class="relative">
        <div class="flex flex-col gap-5">
            <div class="relative">
                <label class="block text-sm font-medium leading-5 text-gray-700" for="email">{{ $t('Email') }}</label>
                <input
                    id="email"
                    v-model="oneTimeCode.email"
                    :placeholder="$t('Email')"
                    class="form-input auth-login-input block w-full mt-1 sm:text-sm sm:leading-5"
                    required
                    type="email"
                />
            </div>
            <div>
                <label class="inline-flex items-center text-sm text-gray-700">
                    <input v-model="oneTimeCode.remember" class="form-checkbox h-4 w-4 text-gray-700" type="checkbox">
                    <span class="ml-2">{{ $t('Stay signed in') }}</span>
                </label>
            </div>
            <div class="pt-2">
                <button id="submit-login-code" class="btn-stage-primary min-h-[3rem] w-full justify-center cursor-pointer" data-style="zoom-in" type="button" @click="submitCodeRequest">
                    {{ $t('Send one-time code') }}
                </button>
            </div>
            <div>
                <button
                    class="inline-flex cursor-pointer items-center gap-1 py-3 text-left text-[0.95rem] leading-[1.6] font-semibold text-[#202830] no-underline hover:text-[#14191f]"
                    type="button"
                    @click="passwordMode = !passwordMode"
                >
                    <span>{{ $t('Sign in with password') }}</span>
                    <svg-vue
                        class="h-3 w-3 text-gray-400 transition-transform duration-200"
                        :class="passwordMode ? 'rotate-180' : ''"
                        icon="font-awesome.regular.angle-down"
                    />
                </button>
                <div v-if="passwordMode" class="pb-1">
                    <p class="text-sm leading-5 text-gray-400">
                        {{ $t('Password login is only available if it has been enabled in the account settings.') }}
                    </p>
                    <div class="relative mt-4">
                        <label class="block text-sm font-medium leading-5 text-gray-700" for="password-email">{{ $t('Email') }}</label>
                        <input
                            id="password-email"
                            v-model="passwordLogin.email"
                            :placeholder="$t('Email')"
                            class="form-input auth-login-input block w-full mt-1 sm:text-sm sm:leading-5"
                            required
                            type="email"
                        />
                    </div>
                    <div class="relative mt-4">
                        <label class="block text-sm font-medium leading-5 text-gray-700" for="password">{{ $t('Password') }}</label>
                        <input
                            id="password"
                            v-model="passwordLogin.password"
                            class="form-input auth-login-input block w-full mt-1 sm:text-sm sm:leading-5"
                            placeholder="******************"
                            required
                            type="password"
                        />
                    </div>
                    <div class="mt-4">
                        <label class="inline-flex items-center text-sm text-gray-700">
                            <input v-model="passwordLogin.remember" class="form-checkbox h-4 w-4 text-gray-700" type="checkbox">
                            <span class="ml-2">{{ $t('Stay signed in') }}</span>
                        </label>
                    </div>
                    <div class="pt-4">
                        <button id="submit-password-login" class="btn-stage-primary min-h-[3rem] w-full justify-center cursor-pointer" type="button" @click="submitPasswordLogin">
                            {{ $t('Sign in with password') }}
                        </button>
                    </div>
                </div>
            </div>
            <div class="flex flex-col gap-3 pt-1.5">
                <p v-if="$store.state.settings.register" class="text-[0.95rem] leading-[1.6] text-ink">
                    {{ $t('Don\'t have an account?') }}
                    <router-link class="font-semibold text-[#202830] no-underline hover:text-[#14191f]" to="/auth/register">
                        {{ $t('Create account') }}
                    </router-link>
                </p>
                <p class="text-[0.95rem] leading-[1.6] text-ink">
                    {{ $t('You can not access your account?') }}<br>
                    <router-link class="font-semibold text-[#202830] no-underline hover:text-[#14191f]" to="/auth/recover">
                        {{ $t('Recover account') }}
                    </router-link>
                </p>
            </div>
        </div>
        <app-dialog
            :open="oneTimeCodeModalOpen"
            :title="$t('Enter one-time code').toString()"
            size="sm"
            :close-on-backdrop="false"
            @close="closeOneTimeCodeModal"
        >
            <div class="vault-otp-group" @paste="handleCodePaste">
                <input
                    v-for="(digit, index) in oneTimeCodeDigits"
                    :key="'login-code-digit-' + index"
                    ref="oneTimeCodeInput"
                    v-model="oneTimeCodeDigits[index]"
                    autocomplete="one-time-code"
                    inputmode="numeric"
                    maxlength="1"
                    type="text"
                    class="vault-otp-input"
                    :disabled="verifyingOneTimeCode"
                    @input="handleCodeInput(index, $event)"
                    @keydown="handleCodeKeydown(index, $event)"
                >
            </div>
            <p class="mt-2 text-xs text-gray-500">
                {{ $t('The code is valid for 10 minutes.') }}
            </p>
        </app-dialog>
    </div>
</template>

<script>
import Cookies from 'js-cookie';
import AppDialog from '@/components/modals/app-dialog.vue';
import {customerHomePath} from "@/utils/access-context";

const PENDING_ONE_TIME_CODE_STORAGE_KEY = 'auth_pending_one_time_code';
const DEFAULT_ONE_TIME_CODE_VALID_MINUTES = 10;

export default {
    name: "login",
    components: {AppDialog},
    metaInfo() {
        return {
            title: this.$i18n.t('Sign In')
        }
    },
    data() {
        return {
            passwordMode: false,
            sendingOneTimeCode: false,
            verifyingOneTimeCode: false,
            oneTimeCodeModalOpen: false,
            oneTimeCodeDigits: ['', '', '', ''],
            oneTimeCodeCooldownUntil: 0,
            oneTimeCodeValidUntil: 0,
            nowTick: Date.now(),
            oneTimeCode: {
                email: null,
                remember: true,
                captcha: null,
            },
            passwordLogin: {
                email: null,
                password: null,
                remember: true,
                captcha: null,
            }
        }
    },
    computed: {
        oneTimeCodeCooldownRemaining() {
            const remaining = Math.ceil((this.oneTimeCodeCooldownUntil - this.nowTick) / 1000);
            return remaining > 0 ? remaining : 0;
        },
        passwordLoginHint() {
            if (this.$store.state.settings && this.$store.state.settings.auth_password_fallback_enabled) {
                return this.$t('Password login is available for users who enabled it in their account settings. The primary administrator can always use password login.');
            }

            return this.$t('Password login is only available for the primary administrator unless it is explicitly enabled in the authentication settings.');
        }
    },
    mounted() {
        this.restorePendingOneTimeCode();
        this.oneTimeCodeTimer = window.setInterval(() => {
            this.nowTick = Date.now();
            if (this.oneTimeCodeValidUntil > 0 && this.nowTick >= this.oneTimeCodeValidUntil) {
                this.clearPendingOneTimeCode();
            }
        }, 1000);
    },
    beforeUnmount() {
        if (this.oneTimeCodeTimer) {
            window.clearInterval(this.oneTimeCodeTimer);
        }
    },
    methods: {
        normalizeEmail(email) {
            return String(email || '').trim();
        },
        isValidEmail(email) {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.normalizeEmail(email));
        },
        notifyInvalidEmailAddress() {
            this.$notify({
                title: this.$i18n.t('Error').toString(),
                text: this.$i18n.t('Please enter a valid email address').toString(),
                type: 'error'
            });
        },
        validateEmailAddress(email) {
            if (this.isValidEmail(email)) {
                return true;
            }

            this.notifyInvalidEmailAddress();
            return false;
        },
        readPendingOneTimeCode() {
            try {
                const raw = sessionStorage.getItem(PENDING_ONE_TIME_CODE_STORAGE_KEY);
                if (!raw) {
                    return null;
                }

                const parsed = JSON.parse(raw);
                return parsed && typeof parsed === 'object' ? parsed : null;
            } catch (error) {
                return null;
            }
        },
        persistPendingOneTimeCode({
            email = null,
            remember = true,
            cooldownUntil = 0,
            validUntil = 0,
        } = {}) {
            const normalizedEmail = String(email || '').trim();
            if (!normalizedEmail || Number(validUntil || 0) <= Date.now()) {
                this.clearPendingOneTimeCode();
                return;
            }

            this.oneTimeCodeValidUntil = Number(validUntil || 0);
            this.oneTimeCodeCooldownUntil = Number(cooldownUntil || 0);

            try {
                sessionStorage.setItem(PENDING_ONE_TIME_CODE_STORAGE_KEY, JSON.stringify({
                    email: normalizedEmail,
                    remember: !!remember,
                    cooldown_until: this.oneTimeCodeCooldownUntil,
                    valid_until: this.oneTimeCodeValidUntil,
                }));
            } catch (error) {
                // Ignore storage errors.
            }
        },
        clearPendingOneTimeCode() {
            this.oneTimeCodeValidUntil = 0;
            this.oneTimeCodeCooldownUntil = 0;

            try {
                sessionStorage.removeItem(PENDING_ONE_TIME_CODE_STORAGE_KEY);
            } catch (error) {
                // Ignore storage errors.
            }
        },
        restorePendingOneTimeCode() {
            const pending = this.readPendingOneTimeCode();
            if (!pending) {
                return;
            }

            const validUntil = Number(pending.valid_until || 0);
            if (validUntil <= Date.now()) {
                this.clearPendingOneTimeCode();
                return;
            }

            this.oneTimeCode.email = String(pending.email || '').trim() || this.oneTimeCode.email;
            this.oneTimeCode.remember = Object.prototype.hasOwnProperty.call(pending, 'remember')
                ? !!pending.remember
                : this.oneTimeCode.remember;
            this.oneTimeCodeValidUntil = validUntil;
            this.oneTimeCodeCooldownUntil = Number(pending.cooldown_until || 0);
            this.oneTimeCodeModalOpen = true;
            this.oneTimeCodeDigits = ['', '', '', ''];
            this.$nextTick(() => this.focusCodeInput(0));
        },
        applyCaptcha(action, callback) {
            if (this.$store.state.settings.recaptcha_enabled) {
                this.$recaptcha(action).then(function (token) {
                    callback(token);
                });
                return;
            }

            callback(null);
        },
        submitCodeRequest() {
            this.oneTimeCode.email = this.normalizeEmail(this.oneTimeCode.email);
            if (!this.validateEmailAddress(this.oneTimeCode.email)) {
                return;
            }

            if (this.oneTimeCodeCooldownRemaining > 0) {
                if (this.oneTimeCodeValidUntil <= Date.now()) {
                    this.oneTimeCodeValidUntil = Date.now() + (DEFAULT_ONE_TIME_CODE_VALID_MINUTES * 60 * 1000);
                }
                this.persistPendingOneTimeCode({
                    email: this.oneTimeCode.email,
                    remember: this.oneTimeCode.remember,
                    cooldownUntil: this.oneTimeCodeCooldownUntil,
                    validUntil: this.oneTimeCodeValidUntil,
                });
                this.oneTimeCodeModalOpen = true;
                this.$nextTick(() => this.focusCodeInput(0));
                return;
            }

            const self = this;
            this.applyCaptcha('login', function (token) {
                self.oneTimeCode.captcha = token;
                self.sendOneTimeCode();
            });
        },
        sendOneTimeCode() {
            const self = this;
            self.sendingOneTimeCode = true;

            axios.post('api/auth/login/code', this.oneTimeCode).then(function (response) {
                const validMinutes = Number(response.data && response.data.valid_minutes ? response.data.valid_minutes : DEFAULT_ONE_TIME_CODE_VALID_MINUTES);
                const cooldownSeconds = Number(response.data && response.data.cooldown_seconds ? response.data.cooldown_seconds : 30);
                const now = Date.now();
                self.oneTimeCodeCooldownUntil = now + (cooldownSeconds * 1000);
                self.oneTimeCodeValidUntil = now + (validMinutes * 60 * 1000);
                self.persistPendingOneTimeCode({
                    email: self.oneTimeCode.email,
                    remember: self.oneTimeCode.remember,
                    cooldownUntil: self.oneTimeCodeCooldownUntil,
                    validUntil: self.oneTimeCodeValidUntil,
                });
                self.oneTimeCodeModalOpen = true;
                self.oneTimeCodeDigits = ['', '', '', ''];
                self.$nextTick(() => self.focusCodeInput(0));
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: response.data && response.data.message
                        ? response.data.message
                        : self.$i18n.t('A one-time code has been sent to your email address').toString(),
                    type: 'success'
                });
            }).catch(function (error) {
                const data = error && error.response ? error.response.data : null;
                if (data && data.cooldown_seconds) {
                    const now = Date.now();
                    self.oneTimeCodeCooldownUntil = now + (Number(data.cooldown_seconds) * 1000);
                    self.oneTimeCodeValidUntil = self.oneTimeCodeValidUntil > now
                        ? self.oneTimeCodeValidUntil
                        : now + (DEFAULT_ONE_TIME_CODE_VALID_MINUTES * 60 * 1000);
                    self.persistPendingOneTimeCode({
                        email: self.oneTimeCode.email,
                        remember: self.oneTimeCode.remember,
                        cooldownUntil: self.oneTimeCodeCooldownUntil,
                        validUntil: self.oneTimeCodeValidUntil,
                    });
                    self.oneTimeCodeModalOpen = true;
                    self.oneTimeCodeDigits = ['', '', '', ''];
                    self.$nextTick(() => self.focusCodeInput(0));
                    return;
                }

                const message = self.getApiErrorMessage(
                    error,
                    self.$i18n.t('The one-time code could not be sent').toString()
                );
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: message,
                    type: 'error'
                });
            }).finally(function () {
                self.sendingOneTimeCode = false;
            });
        },
        submitPasswordLogin() {
            this.passwordLogin.email = this.normalizeEmail(this.passwordLogin.email);
            if (!this.validateEmailAddress(this.passwordLogin.email) || !this.passwordLogin.password) {
                return;
            }

            const self = this;
            this.applyCaptcha('login', function (token) {
                self.passwordLogin.captcha = token;
                self.loginWithPassword();
            });
        },
        loginWithPassword() {
            const self = this;

            axios.post('api/auth/login', this.passwordLogin).then(function (response) {
                self.completeLogin(response.data, !!self.passwordLogin.remember);
            }).catch(function () {
                self.passwordLogin.password = null;
            });
        },
        confirmOneTimeCode() {
            const enteredCode = this.oneTimeCodeDigits.join('');
            if (enteredCode.length !== 4 || this.verifyingOneTimeCode) {
                return;
            }

            const self = this;
            this.verifyingOneTimeCode = true;

            axios.post('api/auth/login/code/verify', {
                email: this.oneTimeCode.email,
                code: enteredCode,
                remember: this.oneTimeCode.remember,
            }).then(function (response) {
                self.completeLogin(response.data, !!self.oneTimeCode.remember);
            }).catch(function (error) {
                const message = self.getApiErrorMessage(
                    error,
                    self.$i18n.t('The entered code is invalid').toString()
                );
                self.$notify({
                    title: self.$i18n.t('Error').toString(),
                    text: message,
                    type: 'error'
                });
                if (
                    (error && error.response && error.response.data && Number(error.response.data.remaining_attempts || 0) === 0)
                    || String(message || '').toLowerCase().includes('expired')
                ) {
                    self.clearPendingOneTimeCode();
                }
                self.oneTimeCodeDigits = ['', '', '', ''];
                self.focusCodeInput(0);
            }).finally(function () {
                self.verifyingOneTimeCode = false;
            });
        },
        completeLogin(response, remember) {
            this.$store.commit('login', {response: response, remember: remember});
            this.clearPendingOneTimeCode();
            this.closeOneTimeCodeModal();
            const accessContext = this.$store && this.$store.state ? this.$store.state.access : null;

            const intendedUrl = Cookies.get('intended_url');
            if (intendedUrl) {
                Cookies.remove('intended_url');

                if (!accessContext.workspace_access && intendedUrl.startsWith('/workspace')) {
                    this.$router.push(customerHomePath(accessContext));
                    return;
                }

                this.$router.push(intendedUrl);
                return;
            }

            this.$router.push(customerHomePath(accessContext));
        },
        closeOneTimeCodeModal() {
            this.oneTimeCodeModalOpen = false;
            this.oneTimeCodeDigits = ['', '', '', ''];
        },
        focusCodeInput(index) {
            this.$nextTick(() => {
                const inputs = this.$refs.oneTimeCodeInput || [];
                if (inputs[index]) {
                    inputs[index].focus();
                    inputs[index].select();
                }
            });
        },
        handleCodeInput(index, event) {
            const value = String(event && event.target ? event.target.value : '').replace(/\D/g, '').slice(-1);
            this.oneTimeCodeDigits[index] = value;

            if (value && index < this.oneTimeCodeDigits.length - 1) {
                this.focusCodeInput(index + 1);
            }

            if (this.oneTimeCodeDigits.join('').length === this.oneTimeCodeDigits.length) {
                this.confirmOneTimeCode();
            }
        },
        handleCodeKeydown(index, event) {
            if (event.key === 'Backspace' && !this.oneTimeCodeDigits[index] && index > 0) {
                this.focusCodeInput(index - 1);
            }
        },
        handleCodePaste(event) {
            const pasted = String(event.clipboardData ? event.clipboardData.getData('text') : '')
                .replace(/\D/g, '')
                .slice(0, this.oneTimeCodeDigits.length)
                .split('');

            if (pasted.length === 0) {
                return;
            }

            event.preventDefault();
            this.oneTimeCodeDigits = this.oneTimeCodeDigits.map(function (digit, index) {
                return pasted[index] || '';
            });

            if (pasted.length >= this.oneTimeCodeDigits.length) {
                this.confirmOneTimeCode();
                return;
            }

            this.focusCodeInput(Math.min(pasted.length, this.oneTimeCodeDigits.length - 1));
        },
        getApiErrorMessage(error, fallback) {
            if (error && error.response && error.response.data && error.response.data.message) {
                return error.response.data.message;
            }

            return fallback;
        }
    }
}
</script>

<style lang="scss">
.auth-login-input,
.auth-login-input:hover,
.auth-login-input:focus {
    background-color: #ffffff !important;
}

.auth-login-input:-webkit-autofill,
.auth-login-input:-webkit-autofill:hover,
.auth-login-input:-webkit-autofill:focus,
.auth-login-input:-webkit-autofill:active {
    -webkit-text-fill-color: var(--ui-text-heading) !important;
    caret-color: var(--ui-text-heading);
    -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
    box-shadow: 0 0 0 1000px #ffffff inset !important;
    transition: background-color 999999s ease-in-out 0s;
}

.vault-otp-group {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0;
}

.vault-otp-input {
    height: 3rem;
    border: 1px solid var(--ui-border-default);
    border-left: 0;
    text-align: center;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--ui-text-strong);
    outline: none;
    border-radius: 0 !important;
}

.vault-otp-input:first-child {
    border-left: 1px solid var(--ui-border-default);
    border-top-left-radius: 0.375rem !important;
    border-bottom-left-radius: 0.375rem !important;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

.vault-otp-input:not(:first-child):not(:last-child) {
    border-radius: 0 !important;
}

.vault-otp-input:last-child {
    border-top-right-radius: 0.375rem !important;
    border-bottom-right-radius: 0.375rem !important;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.vault-otp-input:focus {
    border-color: var(--ui-text-subtle);
    position: relative;
    z-index: 1;
}

</style>
